/**
 * Side panel for one evaluation template: identity form, metadata, visibility, versions.
 * Template examples: NextAdmin `profile/account/page.tsx` (form sections) and `profile/notification/page.tsx`
 * (setting row with a toggle); the panel is the `Sheet`.
 */
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Lock } from 'lucide-react';
import {
  getEvaluationTemplateQueryKey,
  listEvaluationTemplatesQueryKey,
  updateEvaluationTemplateMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { zEvaluationTemplateUpdate } from '../../../generated/api/zod.gen.js';
import { Form, SubmitButton, TextAreaFormField, TextFormField, useZodForm } from '../../../components/form/index.js';
import { Badge, SettingList, SettingRow, Sheet, Toggle, useToast } from '../../../components/ui/index.js';
import { Can } from '../../../components/auth/Can.js';
import type { EvaluationTemplate } from '../../../services/api/evaluation-template.api.js';
import { VersionPanel } from './VersionPanel.js';

interface TemplateDrawerProps {
  template: EvaluationTemplate;
  onClose: () => void;
}

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'neutral' | 'danger'> = {
  active: 'success',
  draft: 'warning',
  deprecated: 'neutral',
  retired: 'neutral',
};

const identitySchema = zEvaluationTemplateUpdate.pick({ name: true, description: true });

export const TemplateDrawer: React.FC<TemplateDrawerProps> = ({ template, onClose }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const update = useMutation(updateEvaluationTemplateMutation());

  const form = useZodForm(identitySchema, {
    defaultValues: { name: template.name ?? '', description: template.description ?? '' },
  });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listEvaluationTemplatesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getEvaluationTemplateQueryKey({ path: { id: template.id } }) }),
    ]);

  const saveIdentity = async (values: { name?: string; description?: string | null }) => {
    await update.mutateAsync({
      path: { id: template.id },
      body: { name: values.name, description: values.description || null },
    });
    await refresh();
    form.reset({ name: values.name ?? '', description: values.description ?? '' });
    notify('Template saved');
  };

  // §2.1: entity visibility is `private` | `org`. `marketplace` is not a visibility — publishing
  // is a listing, which is its own action — so this toggle is "everyone in the org" vs "just me".
  const isOrgWide = template.visibility === 'org';

  const toggleVisibility = () =>
    update.mutate(
      { path: { id: template.id }, body: { visibility: isOrgWide ? 'private' : 'org' } },
      {
        onSuccess: async () => {
          notify(isOrgWide ? 'Template is now private' : 'Template is now org-wide');
          await refresh();
        },
        onError: () => notify('Could not change the visibility', 'danger'),
      },
    );

  return (
    <Sheet
      open
      onClose={onClose}
      width="lg"
      title={template.name ?? `Template #${template.id}`}
      description={
        <span className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANTS[template.status] ?? 'neutral'}>{template.status}</Badge>
          <span className="font-mono text-xs">{template.templateRef ?? `#${template.id}`}</span>
        </span>
      }
    >
      <div className="space-y-6">
        <Form form={form} onSubmit={saveIdentity} className="space-y-4">
          <TextFormField name="name" label="Name" required />
          <TextAreaFormField name="description" label="Description" rows={3} placeholder="Optional description" />
          <Can policy="CanManageEvaluationTemplates">
            <div className="flex justify-end">
              <SubmitButton>Save changes</SubmitButton>
            </div>
          </Can>
        </Form>

        <SettingList>
          <SettingRow title="Subject kind">
            <Badge variant="neutral">{template.subjectKind}</Badge>
          </SettingRow>
          <SettingRow title="Template kind">
            <Badge variant="neutral">{template.templateKind}</Badge>
          </SettingRow>
          <SettingRow title="Created">
            <span className="text-xs text-body">{new Date(template.createdAt).toLocaleDateString()}</span>
          </SettingRow>
          <SettingRow
            title={isOrgWide ? 'Org-wide' : 'Private'}
            icon={isOrgWide ? <Globe size={16} /> : <Lock size={16} />}
            description={
              isOrgWide
                ? 'Everyone in this organization can see and apply it'
                : 'Only you can see it — publish a listing to share it further'
            }
          >
            <Can policy="CanManageEvaluationTemplates">
              <Toggle
                aria-label="Visible to the whole organization"
                checked={isOrgWide}
                onChange={toggleVisibility}
                disabled={update.isPending}
              />
            </Can>
          </SettingRow>
        </SettingList>

        <VersionPanel templateId={template.id} subjectKind={template.subjectKind} />
      </div>
    </Sheet>
  );
};
